let navigationSource = 'route'
const scrollPositions = Object.create(null)
const scrollStorageKey = 'admin-page-scroll-positions'
let stopPendingScrollRestore = null
let stopPendingScrollAnimation = null
const pendingPageStates = Object.create(null)

function getListStateStorageKey() {
  return `admin-list-page-states-${localStorage.getItem('admin-userId') || 'current'}`
}

export function savePageState(route, state) {
  if (!route || !route.name) return
  const scrollTop = saveScrollPosition(route.name)
  try {
    const key = getListStateStorageKey()
    const saved = JSON.parse(localStorage.getItem(key) || '{}')
    saved[route.name] = {fullPath: route.fullPath, state, scrollTop}
    localStorage.setItem(key, JSON.stringify(saved))
  } catch (error) {
    // 存储不可用时不阻止打开详情。
  }
}

export function requestPageStateRestore(route) {
  if (!route || !route.name) return
  delete pendingPageStates[route.name]
  try {
    const saved = JSON.parse(localStorage.getItem(getListStateStorageKey()) || '{}')[route.name]
    if (!saved || saved.fullPath !== route.fullPath || !saved.state) return
    pendingPageStates[route.name] = saved.state
    if (typeof saved.scrollTop === 'number') scrollPositions[route.name] = saved.scrollTop
  } catch (error) {
    // 没有有效记录时使用页面原有的初始化方式。
  }
}

export function consumePageStateRestore(routeName) {
  const state = pendingPageStates[routeName] || null
  delete pendingPageStates[routeName]
  return state
}

export function clearPageStates() {
  Object.keys(pendingPageStates).forEach(key => delete pendingPageStates[key])
  try {
    localStorage.removeItem(getListStateStorageKey())
    sessionStorage.removeItem('orderType')
    sessionStorage.removeItem('reviewActiveName')
  } catch (error) {
    // 存储不可用时仍清除当前标签页待恢复的记录。
  }
}

function getPageScrollContainer() {
  if (typeof document === 'undefined') return null
  const container = document.querySelector('.main-scroll-container')
  if (container && typeof window !== 'undefined') {
    const style = window.getComputedStyle(container)
    const overflow = `${style.overflowX} ${style.overflowY}`
    if (/(auto|scroll|overlay)/.test(overflow)) return container
  }
  return document.scrollingElement || document.documentElement || document.body
}

function isDocumentScrollContainer(container) {
  if (!container || typeof document === 'undefined') return false
  return container === document.scrollingElement ||
    container === document.documentElement ||
    container === document.body
}

export function setNavigationSource(source) {
  navigationSource = source || 'route'
}

export function getNavigationSource() {
  return navigationSource
}

export function consumeNavigationSource() {
  const source = navigationSource
  navigationSource = 'route'
  return source
}

export function saveScrollPosition(routeName) {
  if (!routeName) return
  const scrollContainer = getPageScrollContainer()
  const scrollTop = isDocumentScrollContainer(scrollContainer)
    ? window.pageYOffset || scrollContainer.scrollTop || 0
    : scrollContainer
      ? scrollContainer.scrollTop
      : 0
  scrollPositions[routeName] = scrollTop
  try {
    const saved = JSON.parse(sessionStorage.getItem(scrollStorageKey) || '{}')
    saved[routeName] = scrollTop
    sessionStorage.setItem(scrollStorageKey, JSON.stringify(saved))
  } catch (error) {
    // 存储不可用时仍使用当前标签页内的位置记录。
  }
  return scrollTop
}

export function getScrollPosition(routeName) {
  if (!routeName) return null
  if (typeof scrollPositions[routeName] === 'number') return scrollPositions[routeName]
  try {
    const saved = JSON.parse(sessionStorage.getItem(scrollStorageKey) || '{}')
    return typeof saved[routeName] === 'number' ? saved[routeName] : null
  } catch (error) {
    return null
  }
}

export function setPageScrollPosition(position) {
  if (stopPendingScrollRestore) stopPendingScrollRestore()
  if (stopPendingScrollAnimation) stopPendingScrollAnimation()
  const x = position && typeof position.x === 'number' ? position.x : 0
  const y = position && typeof position.y === 'number' ? position.y : 0
  const scrollContainer = getPageScrollContainer()

  const applyPosition = () => {
    if (isDocumentScrollContainer(scrollContainer) && typeof window !== 'undefined') {
      window.scrollTo(x, y)
    } else if (scrollContainer) {
      scrollContainer.scrollLeft = x
      scrollContainer.scrollTop = y
    }
  }
  applyPosition()
  const scrollElement = scrollContainer || document.scrollingElement
  if (!scrollElement || y <= 0 || scrollElement.scrollTop >= y - 1) return

  // 新标签页返回时列表需要重新加载，等内容高度足够后再恢复。
  let observer = null
  let resizeObserver = null
  const stop = () => {
    if (observer) observer.disconnect()
    if (resizeObserver) resizeObserver.disconnect()
    clearTimeout(timeout)
    if (stopPendingScrollRestore === stop) stopPendingScrollRestore = null
  }
  const retry = () => {
    applyPosition()
    if (scrollElement.scrollTop >= y - 1) stop()
  }
  const timeout = setTimeout(stop, 10000)
  stopPendingScrollRestore = stop
  observer = new MutationObserver(retry)
  const contentRoot = isDocumentScrollContainer(scrollContainer) ? document.body : scrollContainer
  observer.observe(contentRoot || document.body, {childList: true, subtree: true})
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(retry)
    const resizeTarget = isDocumentScrollContainer(scrollContainer)
      ? document.body
      : scrollContainer && (scrollContainer.querySelector('.app-main') || scrollContainer)
    resizeObserver.observe(resizeTarget || document.body)
  }
}

export function smoothPageScrollTo(position, duration = 700) {
  if (stopPendingScrollRestore) stopPendingScrollRestore()
  if (stopPendingScrollAnimation) stopPendingScrollAnimation()

  const scrollContainer = getPageScrollContainer()
  if (!scrollContainer) return

  const isDocument = isDocumentScrollContainer(scrollContainer)
  const startX = isDocument
    ? window.pageXOffset || scrollContainer.scrollLeft || 0
    : scrollContainer.scrollLeft
  const startY = isDocument
    ? window.pageYOffset || scrollContainer.scrollTop || 0
    : scrollContainer.scrollTop
  const targetX = position && typeof position.x === 'number' ? position.x : 0
  const targetY = position && typeof position.y === 'number' ? position.y : 0
  const distanceX = targetX - startX
  const distanceY = targetY - startY

  const applyPosition = (x, y) => {
    if (isDocument) {
      window.scrollTo(x, y)
    } else {
      scrollContainer.scrollLeft = x
      scrollContainer.scrollTop = y
    }
  }

  if ((Math.abs(distanceX) < 1 && Math.abs(distanceY) < 1) ||
    typeof window.requestAnimationFrame !== 'function') {
    applyPosition(targetX, targetY)
    return
  }

  let animationFrame = null
  let startTime = null
  const animationDuration = Math.max(0, Number(duration) || 0)
  const stop = () => {
    if (animationFrame !== null) window.cancelAnimationFrame(animationFrame)
    animationFrame = null
    if (stopPendingScrollAnimation === stop) stopPendingScrollAnimation = null
  }
  const animate = timestamp => {
    if (startTime === null) startTime = timestamp
    const progress = animationDuration === 0
      ? 1
      : Math.min((timestamp - startTime) / animationDuration, 1)
    const easedProgress = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2

    applyPosition(
      startX + distanceX * easedProgress,
      startY + distanceY * easedProgress
    )

    if (progress < 1) {
      animationFrame = window.requestAnimationFrame(animate)
    } else {
      applyPosition(targetX, targetY)
      stopPendingScrollAnimation = null
    }
  }

  stopPendingScrollAnimation = stop
  animationFrame = window.requestAnimationFrame(animate)
}

export function clearScrollPositions() {
  clearPageStates()
  if (stopPendingScrollRestore) stopPendingScrollRestore()
  if (stopPendingScrollAnimation) stopPendingScrollAnimation()
  try {
    sessionStorage.removeItem(scrollStorageKey)
  } catch (error) {
    // 保留内存记录的清理行为。
  }
  Object.keys(scrollPositions).forEach(key => {
    delete scrollPositions[key]
  })
}



// WEBPACK FOOTER //
// ./src/utils/pageNavigation.js